import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrainingDashboard } from '../components/training/TrainingDashboard';
import { CorrectionHistory } from '../components/training/CorrectionHistory';
import { PracticeList } from '../components/training/PracticeList';
import { LearningTrend } from '../components/training/LearningTrend';
import { ProductList } from '../components/training/ProductList';
import { WasteFactorList } from '../components/training/WasteFactorList';
import { useTraining } from '../hooks/useTraining';
import { useAuth } from '../hooks/useAuth';
import styles from './TrainingPage.module.css';

type TrainingTab = 'dashboard' | 'corrections' | 'practices' | 'trends' | 'products' | 'waste-factors';

export default function TrainingPage() {
  const [activeTab, setActiveTab] = useState<TrainingTab>('dashboard');
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const {
    corrections, practices, products, wasteFactors,
    isLoading, error, refresh,
    createPractice, updatePractice, deletePractice,
    createProduct, updateProduct, deleteProduct,
    createWasteFactor, updateWasteFactor, deleteWasteFactor,
    bulkCreatePractices, bulkCreateProducts, bulkCreateWasteFactors,
    resolveCorrection, deleteCorrection,
  } = useTraining();

  return (
    <div className={styles.page}>
      <button className={styles.backLink} onClick={() => navigate('/')}>
        &larr; Back to Chat
      </button>

      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'dashboard' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          Dashboard
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'corrections' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('corrections')}
        >
          Corrections
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'practices' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('practices')}
        >
          Practices
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'products' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('products')}
        >
          Products
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'waste-factors' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('waste-factors')}
        >
          Waste Factors
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'trends' ? styles.activeTab : ''}`}
          onClick={() => setActiveTab('trends')}
        >
          Trends
        </button>
      </div>

      <div className={styles.content}>
        {isLoading && (
          <div className={styles.loadingState} style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
            Loading training data...
          </div>
        )}

        {error && (
          <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--red)', fontSize: '0.875rem' }}>
            Failed to load training data: {error}
            <button onClick={refresh} style={{ marginLeft: '0.5rem', cursor: 'pointer', textDecoration: 'underline', background: 'none', border: 'none', color: 'var(--amber)' }}>
              Retry
            </button>
          </div>
        )}

        {!isLoading && activeTab === 'dashboard' && (
          <TrainingDashboard
            corrections={corrections}
            practices={practices}
            onRefresh={refresh}
          />
        )}
        {!isLoading && activeTab === 'corrections' && (
          <CorrectionHistory
            corrections={corrections}
            onResolve={isAdmin ? resolveCorrection : undefined}
            onDelete={isAdmin ? deleteCorrection : undefined}
          />
        )}
        {!isLoading && activeTab === 'practices' && (
          <PracticeList
            practices={practices}
            onCreatePractice={isAdmin ? createPractice : undefined}
            onUpdatePractice={isAdmin ? updatePractice : undefined}
            onDeletePractice={isAdmin ? deletePractice : undefined}
            onBulkCreate={isAdmin ? bulkCreatePractices : undefined}
          />
        )}
        {!isLoading && activeTab === 'products' && (
          <ProductList
            products={products}
            onCreateProduct={isAdmin ? createProduct : undefined}
            onUpdateProduct={isAdmin ? updateProduct : undefined}
            onDeleteProduct={isAdmin ? deleteProduct : undefined}
            onBulkCreate={isAdmin ? bulkCreateProducts : undefined}
          />
        )}
        {!isLoading && activeTab === 'waste-factors' && (
          <WasteFactorList
            wasteFactors={wasteFactors}
            onCreateWasteFactor={isAdmin ? createWasteFactor : undefined}
            onUpdateWasteFactor={isAdmin ? updateWasteFactor : undefined}
            onDeleteWasteFactor={isAdmin ? deleteWasteFactor : undefined}
            onBulkCreate={isAdmin ? bulkCreateWasteFactors : undefined}
          />
        )}
        {!isLoading && activeTab === 'trends' && (
          <LearningTrend
            corrections={corrections}
            practices={practices}
          />
        )}
      </div>
    </div>
  );
}
